import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiPlus, FiEdit, FiTrash2, FiList, FiMail, FiEye } from 'react-icons/fi';
import { toast } from 'react-toastify';
import Modal from '../../components/Common/Modal';
import { useAuth } from '../../context/AuthContext';
import { useCountry } from '../../context/CountryContext';
import { mailAutoJobsAPI } from '../../services/api';

const MailAutoConfig = () => {
  const { t } = useTranslation();
  const { activeCountry } = useCountry();
  const { canPerformAction } = useAuth();
  const isFranceCRM = String(activeCountry || '').toUpperCase() === 'FRANCE';

  const canCreate = canPerformAction('mail_auto_config', 'create');
  const canUpdate = canPerformAction('mail_auto_config', 'update');
  const canDelete = canPerformAction('mail_auto_config', 'delete');

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logJob, setLogJob] = useState(null);
  const [sendLog, setSendLog] = useState([]);
  const [logLoading, setLogLoading] = useState(false);
  const [devStatus, setDevStatus] = useState(null);
  const [previewJob, setPreviewJob] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const loadJobs = useCallback(async () => {
    try {
      setLoading(true);
      const res = await mailAutoJobsAPI.list();
      setJobs(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || t('mailAutoConfig.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (!isFranceCRM) return;
    loadJobs();
    mailAutoJobsAPI.getDevStatus()
      .then((res) => setDevStatus(res.data || null))
      .catch(() => setDevStatus(null));
  }, [isFranceCRM, loadJobs]);

  const hasActiveQueue = jobs.some(
    (j) =>
      ['queued', 'processing', 'running'].includes(String(j.last_run_status || '')) ||
      Number(j.queue_pending) > 0
  );

  useEffect(() => {
    if (!isFranceCRM || !hasActiveQueue) return undefined;
    const timer = setInterval(() => loadJobs(), 30000);
    return () => clearInterval(timer);
  }, [isFranceCRM, hasActiveQueue, loadJobs]);

  const runProgressLabel = (job) => {
    const pending = Number(job.queue_pending) || 0;
    const failed = Number(job.queue_failed) || 0;
    if (['queued', 'processing', 'running'].includes(String(job.last_run_status || ''))) {
      if (pending > 0) {
        return t('mailAutoConfig.queuePending', { count: pending });
      }
      return t('mailAutoConfig.runProcessing');
    }
    if (pending > 0) return t('mailAutoConfig.queuePending', { count: pending });
    if (failed > 0) return `${failed} failed`;
    return null;
  };

  const openPreview = async (job) => {
    setPreviewJob(job);
    setPreviewData(null);
    setPreviewLoading(true);
    try {
      const res = await mailAutoJobsAPI.previewJob(job.job_id);
      setPreviewData(res.data || null);
    } catch (err) {
      toast.error(err.response?.data?.message || t('mailAutoConfig.previewFailed'));
      setPreviewJob(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDeactivate = async (job) => {
    if (!window.confirm(t('mailAutoConfig.deactivateConfirm'))) return;
    try {
      await mailAutoJobsAPI.delete(job.job_id);
      toast.success(t('mailAutoConfig.deactivated'));
      loadJobs();
    } catch (err) {
      toast.error(err.response?.data?.message || t('common.failed'));
    }
  };

  const openSendLog = async (job) => {
    setLogJob(job);
    setSendLog([]);
    setLogLoading(true);
    try {
      const res = await mailAutoJobsAPI.getSendLog(job.job_id, { limit: 50 });
      setSendLog(res.data?.logs || []);
    } catch (err) {
      toast.error(err.response?.data?.message || t('common.failed'));
    } finally {
      setLogLoading(false);
    }
  };

  const contentLabel = (job) => {
    if (job.content_source === 'custom') return t('mailAutoConfig.customContent');
    return job.template_name || t('mailAutoConfig.template');
  };

  const delaySummary = (job) => {
    const parts = [];
    if (job.recipient_students) {
      parts.push(t('mailAutoConfig.recipientStudentsShort', { days: job.delay_days_students ?? 0 }));
    }
    if (job.recipient_companies) {
      parts.push(t('mailAutoConfig.recipientCompaniesShort', { days: job.delay_days_companies ?? 0 }));
    }
    if (job.recipient_b2b_agencies) {
      parts.push(t('mailAutoConfig.recipientB2bShort', { days: job.delay_days_b2b_agencies ?? 0 }));
    }
    return parts.join(' · ');
  };

  if (!isFranceCRM) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="page-container">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 10, margin: 0 }}>
            <FiMail /> {t('mailAutoConfig.title')}
          </h1>
          <p style={{ margin: '8px 0 0', color: 'var(--text-muted)', fontSize: 14 }}>{t('mailAutoConfig.subtitle')}</p>
        </div>
        {canCreate && (
          <Link to="/mail-auto-config/new" className="btn btn-primary">
            <FiPlus /> {t('mailAutoConfig.newJob')}
          </Link>
        )}
      </div>

      {devStatus?.dev_mode && (
        <div
          className="card"
          style={{
            marginBottom: 16,
            padding: 14,
            fontSize: 13,
            lineHeight: 1.5,
            border: '1px solid #f59e0b',
            background: 'rgba(245, 158, 11, 0.08)',
            color: 'var(--text-primary)'
          }}
        >
          {devStatus.scheduler_enabled
            ? t('mailAutoConfig.devModeBannerSchedulerOn')
            : t('mailAutoConfig.devModeBanner')}
        </div>
      )}

      <div className="card" style={{ marginBottom: 16, padding: 14, fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>
        {t('mailAutoConfig.help')}
      </div>

      {loading ? (
        <div className="loading-spinner"><div className="spinner" /></div>
      ) : jobs.length === 0 ? (
        <div className="card" style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>
          <p style={{ margin: '0 0 16px' }}>{t('mailAutoConfig.noJobs')}</p>
          {canCreate && (
            <Link to="/mail-auto-config/new" className="btn btn-primary">
              <FiPlus /> {t('mailAutoConfig.newJob')}
            </Link>
          )}
        </div>
      ) : (
        <div className="card" style={{ overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
                <th style={{ padding: '10px 12px' }}>{t('mailAutoConfig.jobName')}</th>
                <th style={{ padding: '10px 12px' }}>{t('mailAutoConfig.emailContent')}</th>
                <th style={{ padding: '10px 12px' }}>{t('mailAutoConfig.schedule')}</th>
                <th style={{ padding: '10px 12px' }}>{t('mailAutoConfig.delayDays')}</th>
                <th style={{ padding: '10px 12px' }}>{t('mailAutoConfig.lastRun')}</th>
                <th style={{ padding: '10px 12px' }}>{t('common.status')}</th>
                <th style={{ padding: '10px 12px', width: 120 }} />
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => (
                <tr key={job.job_id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '10px 12px', fontWeight: 600 }}>
                    {canUpdate ? (
                      <Link to={`/mail-auto-config/${job.job_id}/edit`} style={{ color: 'var(--primary)', textDecoration: 'none' }}>
                        {job.name}
                      </Link>
                    ) : (
                      job.name
                    )}
                  </td>
                  <td style={{ padding: '10px 12px' }}>{contentLabel(job)}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <div>{job.send_timezone || 'Europe/Paris'}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{job.send_time || '-'}</div>
                  </td>
                  <td style={{ padding: '10px 12px', maxWidth: 320 }}>{delaySummary(job)}</td>
                  <td style={{ padding: '10px 12px' }}>
                    {job.last_run_date ? (
                      <>
                        <div>{job.last_run_date} ({job.last_run_status || '-'})</div>
                        {runProgressLabel(job) && (
                          <div style={{ fontSize: 12, color: 'var(--primary)', marginTop: 4 }}>
                            {runProgressLabel(job)}
                          </div>
                        )}
                      </>
                    ) : (
                      t('mailAutoConfig.neverRun')
                    )}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <span className={`badge ${job.is_active ? 'badge-success' : 'badge-secondary'}`}>
                      {job.is_active ? t('mailAutoConfig.active') : t('mailAutoConfig.inactive')}
                    </span>
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline"
                        title={t('mailAutoConfig.previewRecipients')}
                        onClick={() => openPreview(job)}
                      >
                        <FiEye />
                      </button>
                      <button type="button" className="btn btn-sm btn-outline" title={t('mailAutoConfig.viewLog')} onClick={() => openSendLog(job)}>
                        <FiList />
                      </button>
                      {canUpdate && (
                        <Link to={`/mail-auto-config/${job.job_id}/edit`} className="btn btn-sm btn-outline" title={t('mailAutoConfig.editJob')}>
                          <FiEdit />
                        </Link>
                      )}
                      {canDelete && job.is_active && (
                        <button type="button" className="btn btn-sm btn-outline" onClick={() => handleDeactivate(job)}>
                          <FiTrash2 />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        isOpen={!!previewJob}
        onClose={() => { setPreviewJob(null); setPreviewData(null); }}
        title={`${t('mailAutoConfig.previewTitle')}${previewJob ? `: ${previewJob.name}` : ''}`}
        size="lg"
      >
        {previewLoading ? (
          <div className="loading-spinner"><div className="spinner" /></div>
        ) : previewData ? (
          <div style={{ fontSize: 13 }}>
            <p style={{ margin: '0 0 12px', fontWeight: 600 }}>
              {t('mailAutoConfig.previewTotal', { count: previewData.counts?.total ?? 0 })}
            </p>
            <p style={{ margin: '0 0 8px', color: 'var(--text-muted)' }}>
              {t('mailAutoConfig.previewRunDate')}: {previewData.run_date} ({previewData.timezone})
            </p>
            {previewData.eligible_created_date && (
              <pre style={{ margin: '0 0 12px', fontSize: 12, background: 'var(--bg-muted)', padding: 10, borderRadius: 6, overflow: 'auto' }}>
                {JSON.stringify(previewData.eligible_created_date, null, 2)}
              </pre>
            )}
            {(previewData.counts?.total ?? 0) === 0 ? (
              <p style={{ color: 'var(--text-muted)' }}>{t('mailAutoConfig.previewEmpty')}</p>
            ) : (
              <>
                <p style={{ margin: '0 0 8px', fontWeight: 600 }}>{t('mailAutoConfig.previewSample')}</p>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      <th style={{ padding: 8, textAlign: 'left' }}>{t('mailAutoConfig.colRecipient')}</th>
                      <th style={{ padding: 8, textAlign: 'left' }}>{t('mailAutoConfig.colEmail')}</th>
                      <th style={{ padding: 8, textAlign: 'left' }}>{t('mailAutoConfig.subject')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(previewData.samples || []).map((row) => (
                      <tr key={`${row.recipient_type}-${row.recipient_id}`} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: 8 }}>{row.recipient_type} #{row.recipient_id}{row.name ? ` — ${row.name}` : ''}</td>
                        <td style={{ padding: 8 }}>{row.email}</td>
                        <td style={{ padding: 8 }}>{row.subject}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
            <p style={{ margin: '12px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>{previewData.note}</p>
          </div>
        ) : null}
      </Modal>

      <Modal
        isOpen={!!logJob}
        onClose={() => setLogJob(null)}
        title={`${t('mailAutoConfig.sendLogTitle')}${logJob ? `: ${logJob.name}` : ''}`}
        size="lg"
      >
        {logLoading ? (
          <div className="loading-spinner"><div className="spinner" /></div>
        ) : sendLog.length === 0 ? (
          <p style={{ color: 'var(--text-muted)' }}>{t('mailAutoConfig.sendLogEmpty')}</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                <th style={{ padding: 8 }}>{t('mailAutoConfig.colRecipient')}</th>
                <th style={{ padding: 8 }}>{t('mailAutoConfig.colEmail')}</th>
                <th style={{ padding: 8 }}>{t('mailAutoConfig.colSentAt')}</th>
              </tr>
            </thead>
            <tbody>
              {sendLog.map((row) => (
                <tr key={row.log_id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: 8 }}>{row.recipient_type} #{row.recipient_id}</td>
                  <td style={{ padding: 8 }}>{row.recipient_email}</td>
                  <td style={{ padding: 8 }}>{row.sent_at ? new Date(row.sent_at).toLocaleString() : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Modal>
    </div>
  );
};

export default MailAutoConfig;
